/**
 * Panneau « Génération » de l'éditeur V2.
 *
 * Permet, depuis l'édition d'un exercice V2 (notamment ceux issus d'une épreuve
 * PDF sans corrigé), de :
 *  - générer le CORRIGÉ (étapes-clés pédagogiques) pour les questions qui n'en
 *    ont pas encore (mode idempotent `only_missing`) ;
 *  - AUDITER le corrigé (vérifie/rectifie les corrections existantes) ;
 *  - générer les DISTRACTEURS pour les corrigés QCM qui n'en ont pas.
 *
 * Réutilise les phases du flow tuteur (`useSolutionPhase` / `useAuditPhase` /
 * `useDistractorsPhase`) et la carte `PhaseCard` du wizard. L'état (ce qui reste
 * à générer) est dérivé des blocs via `v2GenerationState`.
 */

import { useMemo, useState } from 'react';
import { Sparkles, CheckCircle2, RotateCcw } from 'lucide-react';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { v2GenerationState, buildDeleteSubtreeOps } from '@/lib/exercise-v2/block-helpers';
import { useBlocksMutate } from '@/lib/exercise-v2/api';
import {
  useSolutionPhase,
  useDictationPhase,
  useAuditPhase,
  useDistractorsPhase,
  useClueHuntPhase,
} from '@/lib/exercise-v2/use-v2-generation';
import {
  PhaseCard,
  DictationCorrectionControl,
  SolutionSourceToggle,
  type PhaseStatus,
  type SolutionSource,
} from '@/features/exercise-v2-wizard';

export interface V2GenerationPanelProps {
  exerciseId: string;
  blocks: ExerciseBlock[];
}

function errMessage(e: unknown): string | null {
  if (!e) return null;
  return e instanceof Error ? e.message : 'Erreur inattendue';
}

export function V2GenerationPanel({ exerciseId, blocks }: V2GenerationPanelProps) {
  const solution = useSolutionPhase(exerciseId);
  const dictation = useDictationPhase(exerciseId);
  const audit = useAuditPhase(exerciseId);
  const distractors = useDistractorsPhase(exerciseId);
  const clueHunt = useClueHuntPhase(exerciseId);
  const blocksMutate = useBlocksMutate(exerciseId);
  const [forcing, setForcing] = useState(false);
  const [forceError, setForceError] = useState<string | null>(null);
  const [solutionSource, setSolutionSource] = useState<SolutionSource>('generate');

  const state = useMemo(() => v2GenerationState(blocks), [blocks]);
  const anyPending =
    solution.isPending ||
    dictation.isPending ||
    audit.isPending ||
    distractors.isPending ||
    clueHunt.isPending ||
    forcing ||
    blocksMutate.isPending;

  // Nombre de questions qui ont déjà un bloc `clue_hunt` enfant.
  const questionsWithClueHunt = useMemo(
    () =>
      new Set(
        blocks
          .filter((b) => b.block_type === 'clue_hunt' && b.parent_id !== null)
          .map((b) => b.parent_id as string),
      ),
    [blocks],
  );
  const questionsWithoutClueHunt = state.questions.filter(
    (q) => !questionsWithClueHunt.has(q.id),
  ).length;

  async function handleDictate(transcript: string) {
    try {
      await dictation.mutateAsync({ transcript, onlyMissing: true });
    } catch {
      /* surfacé via dictation.error */
    }
  }

  /**
   * Supprime TOUS les corrigés existants (et leurs distracteurs / explications
   * enfants, en cascade) puis régénère le corrigé de zéro. Confirmation requise.
   */
  async function handleForceRegenerate() {
    if (state.corrections.length === 0) return;
    if (
      !window.confirm(
        'Supprimer TOUS les corrigés existants (avec leurs distracteurs et explications) puis les régénérer ?',
      )
    )
      return;
    setForcing(true);
    setForceError(null);
    try {
      const ops = state.corrections.flatMap((c) => buildDeleteSubtreeOps(blocks, c.id));
      if (ops.length > 0) await blocksMutate.mutateAsync(ops);
      await solution.mutateAsync({ onlyMissing: false });
    } catch (e) {
      setForceError(errMessage(e));
    } finally {
      setForcing(false);
    }
  }

  const hasQuestions = state.questions.length > 0;
  const hasCorrections = state.corrections.length > 0;
  const missingCorrections = state.questionsWithoutCorrection.length;
  const missingDistractors = state.qcmWithoutDistractors.length;

  // — Statuts par carte (dérivés de l'état des mutations + des blocs) —
  const solutionStatus: PhaseStatus =
    solution.isPending || dictation.isPending || forcing
      ? 'running'
      : solution.isError || dictation.isError
        ? 'error'
        : hasQuestions && missingCorrections === 0
          ? 'done'
          : 'idle';

  const auditStatus: PhaseStatus = audit.isPending
    ? 'running'
    : audit.isError
      ? 'error'
      : audit.isSuccess
        ? 'done'
        : 'idle';

  const distractorsStatus: PhaseStatus = distractors.isPending
    ? 'running'
    : distractors.isError
      ? 'error'
      : state.qcmCheckpoints.length > 0 && missingDistractors === 0
        ? 'done'
        : 'idle';

  const clueHuntStatus: PhaseStatus = clueHunt.isPending
    ? 'running'
    : clueHunt.isError
      ? 'error'
      : questionsWithClueHunt.size > 0
        ? 'done'
        : 'idle';

  const allDone =
    hasQuestions &&
    hasCorrections &&
    missingCorrections === 0 &&
    state.qcmCheckpoints.length > 0 &&
    missingDistractors === 0;

  return (
    <section className="mb-6">
      <header className="mb-3 flex items-center gap-2">
        <Sparkles className="text-primary-500 h-4 w-4" />
        <h2 className="font-display text-ink-900 text-[15px] font-extrabold">
          Génération assistée
        </h2>
        {allDone && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-sans text-[11px] font-semibold text-emerald-600">
            <CheckCircle2 className="h-3.5 w-3.5" /> Corrigé complet
          </span>
        )}
      </header>

      {!hasQuestions ? (
        <p className="border-ink-200 text-ink-500 rounded-2xl border border-dashed bg-white px-4 py-6 text-center font-sans text-sm">
          Aucune question pour l'instant. Ajoute au moins une question (bloc «&nbsp;question&nbsp;»)
          pour pouvoir générer le corrigé.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <PhaseCard
            index={1}
            title="Corrigé"
            description={
              solutionSource === 'dictation'
                ? 'Dicte le corrigé des questions sans correction.'
                : missingCorrections > 0
                  ? `${missingCorrections} question${missingCorrections > 1 ? 's' : ''} sans corrigé.`
                  : 'Toutes les questions ont un corrigé.'
            }
            status={solutionStatus}
            canRun={solutionSource === 'generate' && !anyPending && missingCorrections > 0}
            onRun={() => solution.mutate({ onlyMissing: true })}
            error={
              solutionSource === 'dictation'
                ? errMessage(dictation.error)
                : (errMessage(solution.error) ?? forceError)
            }
            hideRunButton={solutionSource === 'dictation'}
          >
            <SolutionSourceToggle
              value={solutionSource}
              onChange={setSolutionSource}
              disabled={anyPending}
            />
            {solutionSource === 'dictation' && (
              <DictationCorrectionControl
                onStructure={handleDictate}
                structuring={dictation.isPending}
                structureError={errMessage(dictation.error)}
                disabled={anyPending && !dictation.isPending}
              />
            )}
            {solutionSource === 'generate' && state.corrections.length > 0 && (
              <button
                type="button"
                onClick={() => void handleForceRegenerate()}
                disabled={anyPending}
                className="text-ink-500 hover:text-ink-900 inline-flex min-h-11 items-center gap-1.5 font-sans text-[12px] transition-colors disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {forcing ? 'Régénération…' : 'Forcer la régénération (supprime tout)'}
              </button>
            )}
          </PhaseCard>

          <PhaseCard
            index={2}
            title="Audit"
            description={
              audit.isSuccess
                ? audit.data.fixed_count > 0
                  ? `${audit.data.fixed_count} correction${audit.data.fixed_count > 1 ? 's' : ''} rectifiée${audit.data.fixed_count > 1 ? 's' : ''}.`
                  : 'Corrigé vérifié, rien à corriger.'
                : 'Vérifie l’exactitude des réponses et des explications.'
            }
            status={auditStatus}
            canRun={!anyPending && hasCorrections}
            onRun={() => audit.mutate()}
            error={errMessage(audit.error)}
          />

          <PhaseCard
            index={3}
            title="Distracteurs"
            description={
              state.qcmCheckpoints.length === 0
                ? 'Aucun corrigé QCM pour l’instant.'
                : missingDistractors > 0
                  ? `${missingDistractors} corrigé${missingDistractors > 1 ? 's' : ''} sans distracteur.`
                  : 'Tous les corrigés QCM ont des distracteurs.'
            }
            status={distractorsStatus}
            canRun={!anyPending && missingDistractors > 0}
            onRun={() => distractors.mutate({ onlyMissing: true })}
            error={errMessage(distractors.error)}
          />

          <PhaseCard
            index={4}
            title="Chasse aux indices"
            description={
              !hasQuestions
                ? 'Ajoute au moins une question.'
                : clueHunt.data?.no_clues_emitted
                  ? "Le LLM n'a pas trouvé d'indices pertinents à extraire."
                  : questionsWithClueHunt.size > 0
                    ? `${questionsWithClueHunt.size} question${questionsWithClueHunt.size > 1 ? 's' : ''} avec chasse aux indices.`
                    : `Extrait 2-4 indices cliquables dans l'énoncé (${questionsWithoutClueHunt} question${questionsWithoutClueHunt > 1 ? 's éligibles' : ' éligible'}).`
            }
            status={clueHuntStatus}
            canRun={!anyPending && questionsWithoutClueHunt > 0}
            onRun={() => clueHunt.mutate({})}
            error={errMessage(clueHunt.error)}
          />
        </div>
      )}
    </section>
  );
}
